import {IHaveLanguageText, IHaveLanguageTextChangeEvent, ILanguageTextRow} from "../PA.Core.IF.Aspects";
import {BehaviorSubject} from "rxjs";
import {ESRecord} from "../lib/ESRecord";
import * as _ from "lodash"
import {Language} from "./Language";

export interface ILanguageTextCreateOptions {
    Id?: string,
    Name?: string,
    WbBusinessUnitId?: number,
    LanguageFieldName: string,
    TextFieldName?: string,
    ShortTextFieldName?: string,
    AbbreviationFieldName?: string,
    TooltipFieldName?: string,
    RowKeyFieldName: string
    Rows?: ESRecord[]
    LoadFunction?: (arrLanguages: string[]) => Promise<any>
}

export class LanguageText
    implements
        IHaveLanguageText
{
    private options: ILanguageTextCreateOptions

    private _id: string
    private _name: string
    private _bIsLoaded: boolean
    private _onLoadComplete: Promise<IHaveLanguageText>
    private _languages: string[]

    private _sLanguageFieldName: string

    private _bHasText: boolean
    private _sTextFieldName: string

    private _bHasShortText: boolean;
    private _sShortTextFieldName: string

    private _bHasAbbreviation: boolean;
    private _sAbbreviationFieldName: string

    private _bHasTooltip: boolean;
    private _sTooltipFieldName: string

    private _TextChanged: BehaviorSubject<IHaveLanguageTextChangeEvent>
    private _LanguageTextRows: ILanguageTextRow[]

    private _sRowKeyFieldName: string
    private _dctRecords: {[id:number]: ESRecord }


    constructor(opts: ILanguageTextCreateOptions) {
        this.options = opts
        this._id = opts.Id || "-"
        this._name = opts.Name || ""
        this._TextChanged = new BehaviorSubject<IHaveLanguageTextChangeEvent>(undefined)
        this._LanguageTextRows = []
        this._languages = []
        this._sRowKeyFieldName = opts.RowKeyFieldName
        this._sLanguageFieldName = opts.LanguageFieldName
        this._bHasText = false
        this._bHasShortText = false
        this._bHasAbbreviation = false
        this._bHasTooltip = false
        this._dctRecords = {}
        this._bIsLoaded = false

        if (!_.isEmpty(opts.Rows))
            _.forEach(opts.Rows, (aRow) => {
                if(!aRow.isActive)
                    return

                let key = (<any> aRow)[this._sRowKeyFieldName]
                this._dctRecords[key] = aRow
            })

        if (!_.isEmpty(opts.TextFieldName)) {
            this._sTextFieldName = opts.TextFieldName
            this._bHasText = true
        }

        if (!_.isEmpty(opts.ShortTextFieldName)) {
            this._sShortTextFieldName = opts.ShortTextFieldName
            this._bHasShortText = true
        }

        if (!_.isEmpty(opts.AbbreviationFieldName)) {
            this._sAbbreviationFieldName = opts.AbbreviationFieldName
            this._bHasAbbreviation = true
        }

        if (!_.isEmpty(opts.TooltipFieldName)) {
            this._sTooltipFieldName = opts.TooltipFieldName
            this._bHasTooltip = true
        }

        _.forEach(_.values(this._dctRecords), (aRow) => {
            let lt = new LanguageTextRow(this, aRow)
            this._LanguageTextRows.push(lt)
        })

    }

    get bIsLoaded(): boolean { return this._bIsLoaded  }
    get OnLoadComplete(): Promise<IHaveLanguageText> {
        const self = this

        if (this.bIsLoaded)
            return Promise.resolve(this)

        if (!_.isEmpty(this._onLoadComplete))
            return this._onLoadComplete

        let returnValue = this._onLoadComplete = (async (): Promise<IHaveLanguageText> => {
            //--
            let arrLanguages = await Language.getLanguages(self.options?.WbBusinessUnitId)
            self._languages = arrLanguages
            if (_.isFunction(self.options?.LoadFunction))
                await self.options.LoadFunction(arrLanguages)
            self._bIsLoaded = true

            return self
        })();

        return returnValue;
    }

    get Id(): string { return this._id }
    get Name(): string { return this._name }

    get Languages(): string[] { return this._languages }
    get LanguageTextRows(): ILanguageTextRow[] { return this._LanguageTextRows }

    get TextChanged(): BehaviorSubject<IHaveLanguageTextChangeEvent> { return this._TextChanged }

    get LanguageFieldName(): string { return this._sLanguageFieldName}
    get RowKeyFieldName(): string { return this._sRowKeyFieldName }

    get bHasText(): boolean { return this._bHasText }
    get TextFieldName(): string { return this._sTextFieldName }

    get bHasShortText(): boolean { return this._bHasShortText }
    get ShortTextFieldName(): string { return this._sShortTextFieldName }

    get bHasAbbreviation(): boolean { return this._bHasAbbreviation }
    get AbbreviationFieldName(): string { return this._sAbbreviationFieldName }

    get bHasTooltip(): boolean { return this._bHasTooltip };
    get TooltipFieldName(): string { return this._sTooltipFieldName }

    getTextRow(languageCd: string): ILanguageTextRow {
        for(let aRow of this._LanguageTextRows){
            if(aRow.LanguageCode == languageCd)
                return aRow
        }
        return undefined
    }

    addRow(aRow: ESRecord) {
        if(_.isEmpty(aRow))
            return

        let key = (<any> aRow)[this._sRowKeyFieldName]
        if(key in this._dctRecords)
            return

        this._dctRecords[key] = aRow
        let ltr = new LanguageTextRow(this, aRow)
        this._LanguageTextRows.push(ltr)
        this.TextChanged.next({LanguageText: this, LanguageTextRow: ltr, FieldName: "All"})
    }

}

class LanguageTextRow
    implements
        ILanguageTextRow
{
    _parent: LanguageText = undefined
    _row: ESRecord

    constructor(parent: LanguageText, row: ESRecord){
        this._parent = parent
        this._row = row
    }

    get Parent(): IHaveLanguageText {
        return this._parent
    }

    get LanguageCode(): string {
        if(_.isEmpty(this._row))
            return ""
        // @ts-ignore
        return this._row[this._parent.LanguageFieldName]
    }

    get Row(): ESRecord {
        return this._row
    }

    get Abbreviation(): string {
        if(_.isEmpty(this._row) || !this._parent.bHasAbbreviation)
            return ""

        // @ts-ignore
        return this._row[this._parent.AbbreviationFieldName]
    }

    set Abbreviation(value: string) {
        if(_.isEmpty(this._row) || !this._parent.bHasAbbreviation)
            return

        // @ts-ignore
        this._row[this._parent.AbbreviationFieldName] = value

        this._parent.TextChanged.next({LanguageText: this._parent, LanguageTextRow: this, FieldName: "Abbreviation"})
    }

    get ShortText(): string {
        if(_.isEmpty(this._row) || !this._parent.bHasShortText)
            return ""

        // @ts-ignore
        return this._row[this._parent.ShortTextFieldName]
    }

    set ShortText(value: string) {
        if(_.isEmpty(this._row) || !this._parent.bHasShortText)
            return

        // @ts-ignore
        this._row[this._parent.ShortTextFieldName] = value

        this._parent.TextChanged.next({LanguageText: this._parent, LanguageTextRow: this, FieldName: "ShortText"})
    }

    get Text(): string {
        if(_.isEmpty(this._row) || !this._parent.bHasText)
            return ""

        // @ts-ignore
        return this._row[this._parent.TextFieldName]
    }

    set Text(value: string) {
        if(_.isEmpty(this._row) || !this._parent.bHasText)
            return

        // @ts-ignore
        this._row[this._parent.TextFieldName] = value

        this._parent.TextChanged.next({LanguageText: this._parent, LanguageTextRow: this, FieldName: "Text"})
    }


    get Tooltip(): string {
        if(_.isEmpty(this._row) || !this._parent.bHasTooltip)
            return ""

        // @ts-ignore
        return this._row[this._parent.TooltipFieldName]
    }

    set Tooltip(value: string) {
        if(_.isEmpty(this._row) || !this._parent.bHasTooltip)
            return

        // @ts-ignore
        this._row[this._parent.TooltipFieldName] = value

        this._parent.TextChanged.next({LanguageText: this._parent, LanguageTextRow: this, FieldName: "Tooltip"})
    }

}